/**
 * Interaction Manager - Handles all entity interactions in the game
 * 
 * Features:
 * - Proximity-based interactions
 * - Raycast/look-at interactions
 * - Click/button interactions
 * - Trigger zone interactions
 * - Entity-to-entity interactions
 * - Interaction UI prompts
 * - Cooldown management
 * - Priority-based interaction selection
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Ray } from "@babylonjs/core/Culling/ray";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import { Color3 } from "@babylonjs/core/Maths/math.color";

import { IDisposable, IUpdatable } from "../core/Engine.types";
import { Entity } from "../entity/Entity";
import { EntityManager } from "../entity/EntityManager";
import { InputManager } from "../input/InputManager";
import {
    InteractionTriggerType,
    InteractableCategory,
    InteractionPriority,
    InteractionResult,
    InteractableConfig,
    InteractorConfig,
    InteractionEvent,
    InteractionHandler,
    ProximityResult,
    RaycastInteractionResult,
    InteractionState,
    InteractionManagerConfig,
    InteractionEventType,
    InteractionEventCallback,
} from "./Interaction.types";

/**
 * Default interactable configuration
 */
const DEFAULT_INTERACTABLE_CONFIG: InteractableConfig = {
    enabled: true,
    category: InteractableCategory.ENVIRONMENT,
    triggerTypes: [InteractionTriggerType.CLICK, InteractionTriggerType.PROXIMITY],
    interactionRadius: 3.0,
    priority: InteractionPriority.NORMAL,
    highlightOnHover: true,
    showPrompt: true,
    cooldown: 500,
    exclusiveInteraction: false,
    tags: [],
};

/**
 * Default interactor configuration
 */
const DEFAULT_INTERACTOR_CONFIG: InteractorConfig = {
    enabled: true,
    raycastDistance: 10.0,
    proximityRadius: 5.0,
    maxSimultaneousInteractions: 1,
    allowedCategories: Object.values(InteractableCategory) as InteractableCategory[],
    ignoredCategories: [],
    requiredTags: [],
    ignoredTags: [],
    autoInteract: false,
};

/**
 * Default manager configuration
 */
const DEFAULT_MANAGER_CONFIG: InteractionManagerConfig = {
    enabled: true,
    proximityCheckInterval: 100,
    defaultRaycastDistance: 10.0,
    maxProximityEntities: 20,
    debug: false,
    globalFilters: [],
    defaultHighlightColor: { r: 1, g: 0.8, b: 0.2, a: 1 },
    autoShowPrompts: true,
};

/**
 * Registered interactable entity data
 */
interface RegisteredInteractable {
    entity: Entity;
    config: InteractableConfig;
    handlers: Map<InteractionTriggerType, InteractionHandler[]>;
    state: InteractionState;
}

/**
 * Registered interactor entity data
 */
interface RegisteredInteractor {
    entity: Entity;
    config: InteractorConfig;
    state: InteractionState;
}

/**
 * InteractionManager - Central manager for all entity interactions
 */
export class InteractionManager implements IUpdatable, IDisposable {
    private _scene: Scene;
    private _inputManager: InputManager | null = null;
    private _config: InteractionManagerConfig;

    // Registered entities
    private _interactables: Map<string, RegisteredInteractable> = new Map();
    private _interactors: Map<string, RegisteredInteractor> = new Map();

    // Highlight layer for visual feedback
    private _highlightLayer: HighlightLayer | null = null;
    private _currentHighlighted: Entity | null = null;

    // Event system
    private _eventCallbacks: Map<InteractionEventType, Set<InteractionEventCallback>> = new Map();

    // Proximity check timer
    private _lastProximityCheck: number = 0;

    // Current interaction target (for UI prompts)
    private _currentInteractionTarget: Entity | null = null;
    private _currentInteractionPrompt: string | null = null;

    /**
     * Get current interaction target
     */
    public get currentTarget(): Entity | null {
        return this._currentInteractionTarget;
    }

    /**
     * Get current interaction prompt
     */
    public get currentPrompt(): string | null {
        return this._currentInteractionPrompt;
    }

    /**
     * Check if interactions are enabled
     */
    public get enabled(): boolean {
        return this._config.enabled;
    }

    public set enabled(value: boolean) {
        this._config.enabled = value;
    }

    constructor(
        scene: Scene,
        _entityManager: EntityManager,
        config?: Partial<InteractionManagerConfig>
    ) {
        this._scene = scene;
        this._config = { ...DEFAULT_MANAGER_CONFIG, ...config };

        // Create highlight layer
        this._highlightLayer = new HighlightLayer("interactionHighlight", scene);
        this._highlightLayer.innerGlow = false;
        this._highlightLayer.outerGlow = true;

        console.log("InteractionManager: Initialized");
    }

    /**
     * Set input manager for input-based interactions
     */
    public setInputManager(inputManager: InputManager): void {
        this._inputManager = inputManager;
    }

    /**
     * Register an entity as interactable
     */
    public registerInteractable(
        entity: Entity,
        config?: Partial<InteractableConfig>
    ): void {
        const fullConfig: InteractableConfig = { ...DEFAULT_INTERACTABLE_CONFIG, ...config };

        const registered: RegisteredInteractable = {
            entity,
            config: fullConfig,
            handlers: new Map(),
            state: this._createEmptyState(),
        };

        this._interactables.set(entity.id, registered);
        console.log(`InteractionManager: Registered interactable "${entity.name}" (${entity.id})`);
    }

    /**
     * Register an entity as an interactor (can interact with others)
     */
    public registerInteractor(
        entity: Entity,
        config?: Partial<InteractorConfig>
    ): void {
        const fullConfig: InteractorConfig = { ...DEFAULT_INTERACTOR_CONFIG, ...config };

        const registered: RegisteredInteractor = {
            entity,
            config: fullConfig,
            state: this._createEmptyState(),
        };

        this._interactors.set(entity.id, registered);
        console.log(`InteractionManager: Registered interactor "${entity.name}" (${entity.id})`);
    }

    /**
     * Unregister an interactable
     */
    public unregisterInteractable(entityId: string): void {
        const registered = this._interactables.get(entityId);
        if (registered) {
            this._removeHighlight(registered.entity);
            this._interactables.delete(entityId);
            console.log(`InteractionManager: Unregistered interactable ${entityId}`);
        }
    }

    /**
     * Unregister an interactor
     */
    public unregisterInteractor(entityId: string): void {
        this._interactors.delete(entityId);
        console.log(`InteractionManager: Unregistered interactor ${entityId}`);
    }

    /**
     * Add interaction handler for an interactable
     */
    public addInteractionHandler(
        entityId: string,
        triggerType: InteractionTriggerType,
        handler: InteractionHandler
    ): void {
        const registered = this._interactables.get(entityId);
        if (!registered) {
            console.warn(`InteractionManager: Entity ${entityId} not registered as interactable`);
            return;
        }

        if (!registered.handlers.has(triggerType)) {
            registered.handlers.set(triggerType, []);
        }
        registered.handlers.get(triggerType)!.push(handler);
    }

    /**
     * Remove interaction handler
     */
    public removeInteractionHandler(
        entityId: string,
        triggerType: InteractionTriggerType,
        handler: InteractionHandler
    ): void {
        const registered = this._interactables.get(entityId);
        if (!registered) return;

        const handlers = registered.handlers.get(triggerType);
        if (handlers) {
            const index = handlers.indexOf(handler);
            if (index !== -1) {
                handlers.splice(index, 1);
            }
        }
    }

    /**
     * Try to interact with an entity
     */
    public async interact(
        initiatorId: string,
        targetId: string,
        triggerType: InteractionTriggerType,
        data?: Record<string, unknown>
    ): Promise<InteractionResult> {
        if (!this._config.enabled) {
            return InteractionResult.BLOCKED;
        }

        const interactor = this._interactors.get(initiatorId);
        const interactable = this._interactables.get(targetId);

        if (!interactor || !interactable) {
            return InteractionResult.NONE;
        }

        // Check if interaction is valid
        if (!this._canInteract(interactor, interactable, triggerType)) {
            return InteractionResult.BLOCKED;
        }

        // Check cooldown
        if (interactable.state.onCooldown && Date.now() < interactable.state.cooldownEndTime) {
            return InteractionResult.BLOCKED;
        }

        // Create interaction event
        const event: InteractionEvent = {
            triggerType,
            initiator: interactor.entity,
            target: interactable.entity,
            position: interactable.entity.position.clone(),
            distance: Vector3.Distance(interactor.entity.position, interactable.entity.position),
            timestamp: Date.now(),
            result: InteractionResult.PENDING,
            data,
        };

        // Emit start event
        this._emitEvent("interaction_start", event);

        // Execute handlers
        let result = InteractionResult.SUCCESS;
        const handlers = interactable.handlers.get(triggerType);
        
        if (handlers && handlers.length > 0) {
            for (const handler of handlers) {
                try {
                    const handlerResult = await handler(event);
                    if (handlerResult === InteractionResult.FAILED || handlerResult === InteractionResult.BLOCKED) {
                        result = handlerResult;
                        break;
                    }
                } catch (error) {
                    console.error(`InteractionManager: Handler error for ${targetId}:`, error);
                    result = InteractionResult.FAILED;
                    break;
                }
            }
        }

        // Update event result
        event.result = result;

        // Apply cooldown
        if (result === InteractionResult.SUCCESS && interactable.config.cooldown > 0) {
            interactable.state.onCooldown = true;
            interactable.state.cooldownEndTime = Date.now() + interactable.config.cooldown;
            this._emitEvent("cooldown_start", event);
        }

        // Update interaction state
        if (result === InteractionResult.SUCCESS) {
            interactable.state.currentInteractions.add(initiatorId);
            interactable.state.isBeingInteractedWith = true;
            interactable.state.interactingEntity = initiatorId;
            this._emitEvent("interaction_success", event);
        } else {
            this._emitEvent("interaction_failed", event);
        }

        // Emit end event
        this._emitEvent("interaction_end", event);

        return result;
    }

    /**
     * End an ongoing interaction
     */
    public endInteraction(initiatorId: string, targetId: string): void {
        const interactable = this._interactables.get(targetId);
        if (!interactable) return;

        interactable.state.currentInteractions.delete(initiatorId);
        if (interactable.state.interactingEntity === initiatorId) {
            interactable.state.isBeingInteractedWith = false;
            interactable.state.interactingEntity = null;
        }
    }

    /**
     * Perform raycast interaction check
     */
    public raycastInteraction(
        origin: Vector3,
        direction: Vector3,
        maxDistance?: number
    ): RaycastInteractionResult {
        const distance = maxDistance ?? this._config.defaultRaycastDistance;
        const ray = new Ray(origin, direction, distance);

        // Get all meshes from interactables
        const interactableMeshes: AbstractMesh[] = [];
        const meshToEntity: Map<AbstractMesh, Entity> = new Map();

        for (const [, registered] of this._interactables) {
            if (!registered.config.enabled) continue;
            if (!registered.config.triggerTypes.includes(InteractionTriggerType.RAYCAST)) continue;

            const meshes = registered.entity.meshes;
            for (const mesh of meshes) {
                if (mesh.isPickable) {
                    interactableMeshes.push(mesh);
                    meshToEntity.set(mesh, registered.entity);
                }
            }
        }

        // Perform pick
        const pickInfo = this._scene.pickWithRay(ray, (mesh) => interactableMeshes.includes(mesh));

        if (pickInfo && pickInfo.hit && pickInfo.pickedMesh) {
            const entity = meshToEntity.get(pickInfo.pickedMesh);
            if (entity) {
                return {
                    hit: true,
                    entity,
                    distance: pickInfo.distance,
                    hitPoint: pickInfo.pickedPoint ?? undefined,
                    hitNormal: pickInfo.getNormal() ?? undefined,
                    mesh: pickInfo.pickedMesh,
                };
            }
        }

        return {
            hit: false,
            distance: distance,
        };
    }

    /**
     * Get all interactables within range of an entity
     */
    public getInteractablesInRange(
        entityId: string,
        range?: number
    ): ProximityResult[] {
        const interactor = this._interactors.get(entityId);
        if (!interactor) return [];

        const radius = range ?? interactor.config.proximityRadius;
        const results: ProximityResult[] = [];
        const interactorPos = interactor.entity.position;

        for (const [, registered] of this._interactables) {
            if (!registered.config.enabled) continue;
            if (registered.entity.id === entityId) continue;

            const distance = Vector3.Distance(interactorPos, registered.entity.position);
            const isInRange = distance <= Math.min(radius, registered.config.interactionRadius);
            const wasInRange = registered.state.proximityEntities.has(entityId);

            results.push({
                entity: registered.entity,
                distance,
                isInRange,
                wasInRange,
                justEntered: isInRange && !wasInRange,
                justExited: !isInRange && wasInRange,
            });
        }

        // Sort by distance
        results.sort((a, b) => a.distance - b.distance);

        return results;
    }

    /**
     * Get the closest interactable to an entity
     */
    public getClosestInteractable(
        entityId: string,
        filter?: (entity: Entity) => boolean
    ): { entity: Entity; distance: number } | null {
        const results = this.getInteractablesInRange(entityId);
        
        for (const result of results) {
            if (result.isInRange) {
                if (!filter || filter(result.entity as Entity)) {
                    return {
                        entity: result.entity as Entity,
                        distance: result.distance,
                    };
                }
            }
        }

        return null;
    }

    /**
     * Subscribe to interaction events
     */
    public on(eventType: InteractionEventType, callback: InteractionEventCallback): void {
        if (!this._eventCallbacks.has(eventType)) {
            this._eventCallbacks.set(eventType, new Set());
        }
        this._eventCallbacks.get(eventType)!.add(callback);
    }

    /**
     * Unsubscribe from interaction events
     */
    public off(eventType: InteractionEventType, callback: InteractionEventCallback): void {
        const callbacks = this._eventCallbacks.get(eventType);
        if (callbacks) {
            callbacks.delete(callback);
        }
    }

    /**
     * Update interaction system
     */
    public update(_deltaTime: number): void {
        if (!this._config.enabled) return;

        const now = Date.now();

        // Periodic proximity checks
        if (now - this._lastProximityCheck >= this._config.proximityCheckInterval) {
            this._updateProximity();
            this._lastProximityCheck = now;
        }

        // Update cooldowns
        this._updateCooldowns(now);

        // Handle input-based interactions
        if (this._inputManager) {
            this._handleInputInteractions();
        }

        // Update highlight
        this._updateHighlight();
    }

    /**
     * Dispose interaction manager
     */
    public dispose(): void {
        if (this._highlightLayer) {
            this._highlightLayer.dispose();
            this._highlightLayer = null;
        }

        this._interactables.clear();
        this._interactors.clear();
        this._eventCallbacks.clear();

        console.log("InteractionManager: Disposed");
    }

    /**
     * Create empty interaction state
     */
    private _createEmptyState(): InteractionState {
        return {
            currentInteractions: new Set(),
            proximityEntities: new Map(),
            highlightedEntity: null,
            onCooldown: false,
            cooldownEndTime: 0,
            isBeingInteractedWith: false,
            interactingEntity: null,
        };
    }

    /**
     * Check if an interactor can interact with an interactable
     */
    private _canInteract(
        interactor: RegisteredInteractor,
        interactable: RegisteredInteractable,
        triggerType: InteractionTriggerType
    ): boolean {
        // Check enabled states
        if (!interactor.config.enabled || !interactable.config.enabled) {
            return false;
        }

        // Check trigger type support
        if (!interactable.config.triggerTypes.includes(triggerType)) {
            return false;
        }

        // Check category filters
        if (!interactor.config.allowedCategories.includes(interactable.config.category)) {
            return false;
        }
        if (interactor.config.ignoredCategories.includes(interactable.config.category)) {
            return false;
        }

        // Check exclusive interaction
        if (interactable.config.exclusiveInteraction && interactable.state.isBeingInteractedWith) {
            return false;
        }

        // Check tag requirements
        for (const tag of interactor.config.requiredTags) {
            if (!interactable.config.tags.includes(tag)) {
                return false;
            }
        }

        // Check ignored tags
        for (const tag of interactor.config.ignoredTags) {
            if (interactable.config.tags.includes(tag)) {
                return false;
            }
        }

        // Run global filters
        for (const filter of this._config.globalFilters) {
            if (!filter(interactor.entity, interactable.entity)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Update proximity detection
     */
    private _updateProximity(): void {
        for (const [interactorId, interactor] of this._interactors) {
            if (!interactor.config.enabled) continue;

            const results = this.getInteractablesInRange(interactorId);

            for (const result of results) {
                const interactable = this._interactables.get(result.entity.id);
                if (!interactable) continue;

                // Update proximity state
                if (result.isInRange) {
                    interactable.state.proximityEntities.set(interactorId, result);
                } else {
                    interactable.state.proximityEntities.delete(interactorId);
                }

                // Emit proximity events
                if (result.justEntered) {
                    const event = this._createProximityEvent(interactor.entity, interactable.entity, result);
                    this._emitEvent("proximity_enter", event);

                    // Trigger proximity interaction
                    if (interactable.config.triggerTypes.includes(InteractionTriggerType.PROXIMITY)) {
                        this.interact(interactorId, result.entity.id, InteractionTriggerType.PROXIMITY);
                    }
                }

                if (result.justExited) {
                    const event = this._createProximityEvent(interactor.entity, interactable.entity, result);
                    this._emitEvent("proximity_exit", event);
                }
            }
        }
    }

    /**
     * Update cooldown timers
     */
    private _updateCooldowns(now: number): void {
        for (const [, registered] of this._interactables) {
            if (registered.state.onCooldown && now >= registered.state.cooldownEndTime) {
                registered.state.onCooldown = false;
                
                const event: InteractionEvent = {
                    triggerType: InteractionTriggerType.AUTOMATIC,
                    initiator: registered.entity,
                    target: registered.entity,
                    position: registered.entity.position,
                    distance: 0,
                    timestamp: now,
                    result: InteractionResult.SUCCESS,
                };
                this._emitEvent("cooldown_end", event);
            }
        }
    }

    /**
     * Handle input-based interactions
     */
    private _handleInputInteractions(): void {
        if (!this._inputManager) return;

        // Find the best interaction target for each interactor
        for (const [interactorId, interactor] of this._interactors) {
            if (!interactor.config.enabled) continue;

            const closest = this.getClosestInteractable(interactorId);
            
            if (closest) {
                this._currentInteractionTarget = closest.entity;
                const registered = this._interactables.get(closest.entity.id);
                if (registered && registered.config.showPrompt) {
                    this._currentInteractionPrompt = registered.config.interactionPrompt || 
                        `Press E to interact with ${registered.config.displayName || closest.entity.name}`;
                }

                // Check for interaction key press (E key)
                if (this._inputManager.wasKeyJustPressed("e")) {
                    this.interact(interactorId, closest.entity.id, InteractionTriggerType.CLICK);
                }
            } else {
                this._currentInteractionTarget = null;
                this._currentInteractionPrompt = null;
            }
        }
    }

    /**
     * Update highlight visuals
     */
    private _updateHighlight(): void {
        if (!this._highlightLayer) return;

        // Find the entity that should be highlighted
        let targetEntity: Entity | null = null;
        let highestPriority = -1;

        for (const [, interactor] of this._interactors) {
            if (!interactor.config.enabled) continue;

            const closest = this.getClosestInteractable(interactor.entity.id);
            if (closest) {
                const registered = this._interactables.get(closest.entity.id);
                if (registered && registered.config.highlightOnHover) {
                    if (registered.config.priority > highestPriority) {
                        targetEntity = closest.entity;
                        highestPriority = registered.config.priority;
                    }
                }
            }
        }

        // Update highlight
        if (targetEntity !== this._currentHighlighted) {
            // Remove old highlight
            if (this._currentHighlighted) {
                this._removeHighlight(this._currentHighlighted);
            }

            // Add new highlight
            if (targetEntity) {
                this._addHighlight(targetEntity);
            }

            this._currentHighlighted = targetEntity;
        }
    }

    /**
     * Add highlight to entity
     */
    private _addHighlight(entity: Entity): void {
        if (!this._highlightLayer) return;

        const registered = this._interactables.get(entity.id);
        const color = registered?.config.highlightColor ?? this._config.defaultHighlightColor;
        const babylonColor = new Color3(color.r, color.g, color.b);

        for (const mesh of entity.meshes) {
            if (mesh instanceof Mesh) {
                this._highlightLayer.addMesh(mesh, babylonColor);
            }
        }

        // Emit highlight start event
        const event: InteractionEvent = {
            triggerType: InteractionTriggerType.AUTOMATIC,
            initiator: entity,
            target: entity,
            position: entity.position,
            distance: 0,
            timestamp: Date.now(),
            result: InteractionResult.SUCCESS,
        };
        this._emitEvent("highlight_start", event);
    }

    /**
     * Remove highlight from entity
     */
    private _removeHighlight(entity: Entity): void {
        if (!this._highlightLayer) return;

        for (const mesh of entity.meshes) {
            if (mesh instanceof Mesh) {
                this._highlightLayer.removeMesh(mesh);
            }
        }

        // Emit highlight end event
        const event: InteractionEvent = {
            triggerType: InteractionTriggerType.AUTOMATIC,
            initiator: entity,
            target: entity,
            position: entity.position,
            distance: 0,
            timestamp: Date.now(),
            result: InteractionResult.SUCCESS,
        };
        this._emitEvent("highlight_end", event);
    }

    /**
     * Create proximity event
     */
    private _createProximityEvent(initiator: Entity, target: Entity, result: ProximityResult): InteractionEvent {
        return {
            triggerType: InteractionTriggerType.PROXIMITY,
            initiator,
            target,
            position: target.position.clone(),
            distance: result.distance,
            timestamp: Date.now(),
            result: InteractionResult.SUCCESS,
        };
    }

    /**
     * Emit interaction event
     */
    private _emitEvent(eventType: InteractionEventType, event: InteractionEvent): void {
        const callbacks = this._eventCallbacks.get(eventType);
        if (callbacks) {
            for (const callback of callbacks) {
                try {
                    callback(event, eventType);
                } catch (error) {
                    console.error(`InteractionManager: Event callback error:`, error);
                }
            }
        }
    }
}
